/**
 * PATH: apps/quote/src/pages/claims/claim-editor-page/evidence/EmailEvidencePicker.tsx
 * FILE_ID: QUOTE-CLAIMS-EVIDENCE-EMAIL-001
 * PURPOSE: Select a recent or searched mailbox message as claim evidence.
 * WHY: Reuse email-hub's canonical mailbox list/search contract.
 */

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Loader2, Mail, Search, X } from 'lucide-react'
import {
  fetchRecentEmails,
  searchMailbox,
  type LinkedEmailRow,
} from '@quotech/email-hub'

interface EmailEvidencePickerProps {
  open: boolean
  excludedIds: ReadonlySet<string>
  onClose: () => void
  onSelect: (email: LinkedEmailRow) => Promise<void>
}

export function EmailEvidencePicker({
  open,
  excludedIds,
  onClose,
  onSelect,
}: EmailEvidencePickerProps) {
  const [query, setQuery] = useState('')
  const [selectingId, setSelectingId] = useState<string | null>(null)
  const [selectError, setSelectError] = useState<string | null>(null)
  const trimmed = query.trim()
  const searchActive = trimmed.length >= 2

  const recent = useQuery({
    queryKey: ['claim-evidence-recent-mailbox'],
    queryFn: fetchRecentEmails,
    enabled: open && !searchActive,
    staleTime: 30_000,
  })
  const search = useQuery({
    queryKey: ['claim-evidence-mailbox-search', trimmed],
    queryFn: () => searchMailbox(trimmed),
    enabled: open && searchActive,
    staleTime: 30_000,
  })

  if (!open) return null

  const activeQuery = searchActive ? search : recent
  const rows = (activeQuery.data ?? []).filter(
    (email) => !excludedIds.has(email.id),
  )

  const selectEmail = async (email: LinkedEmailRow) => {
    setSelectingId(email.id)
    setSelectError(null)
    try {
      await onSelect(email)
      setQuery('')
      onClose()
    } catch (error) {
      setSelectError(
        error instanceof Error ? error.message : 'Could not add email evidence.',
      )
    } finally {
      setSelectingId(null)
    }
  }

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 p-4">
      <div className="flex max-h-[80vh] w-full max-w-xl flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-950">
        <div className="flex items-start justify-between border-b border-slate-100 px-5 py-4 dark:border-slate-800">
          <div>
            <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
              Add email evidence
            </h2>
            <p className="mt-1 text-xs text-slate-500">
              Select a recent message or search sender, subject or body.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close email evidence picker"
            className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="border-b border-slate-100 px-5 py-3 dark:border-slate-800">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              autoFocus
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search mailbox..."
              className="h-9 w-full rounded-lg border border-slate-200 bg-white pl-9 pr-3 text-sm outline-none focus:border-amber-500 dark:border-slate-700 dark:bg-slate-900"
            />
          </div>
        </div>
        <div className="min-h-[240px] overflow-y-auto">
          {activeQuery.isFetching ? (
            <div className="flex items-center justify-center gap-2 py-12 text-sm text-slate-400">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading mailbox...
            </div>
          ) : activeQuery.error ? (
            <div className="px-5 py-12 text-center text-sm text-rose-600">
              Mailbox search failed. Try again.
            </div>
          ) : rows.length === 0 ? (
            <div className="px-5 py-12 text-center text-sm text-slate-500">
              {searchActive
                ? 'No matching messages available.'
                : 'No recent messages available.'}
            </div>
          ) : (
            <ul className="divide-y divide-slate-100 dark:divide-slate-800">
              {rows.map((email) => (
                <li key={email.id}>
                  <button
                    type="button"
                    disabled={Boolean(selectingId)}
                    onClick={() => void selectEmail(email)}
                    className="flex w-full items-start gap-3 px-5 py-3 text-left hover:bg-slate-50 disabled:opacity-60 dark:hover:bg-slate-900"
                  >
                    {selectingId === email.id
                      ? <Loader2 className="mt-0.5 h-5 w-5 animate-spin text-amber-500" />
                      : <Mail className="mt-0.5 h-5 w-5 text-slate-400" />}
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-slate-800 dark:text-slate-100">
                        {email.subject || '(No subject)'}
                      </span>
                      <span className="block truncate text-xs text-slate-500">
                        {email.from_name || email.from_address}
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        {selectError && (
          <div className="border-t border-rose-100 bg-rose-50 px-5 py-3 text-xs text-rose-600">
            {selectError}
          </div>
        )}
      </div>
    </div>
  )
}
